/* Tenzen - multi-currency display + lazy flag loader for the country picker.
 *
 * Spec (locked 2026-05-28):
 *
 *   Visitor country → native currency display on PDP / cards / cart.
 *   Checkout remains INR (Shopify Payments unavailable in India, so true
 *   multi-currency checkout impossible - this is display-only).
 *
 *   Layout (server renders all prices as: <main> / <compare>):
 *     IN visitor → INR <inr> / ¥ <2× INR jpy_rate>           (server default, no-op)
 *     JP visitor → ¥ <2× INR jpy_rate> / USD <rounded>       (MAIN swapped; native is yen)
 *     any other  → <NATIVE> <rounded> / ¥ <2× INR jpy_rate>  (LEFT half rewritten to native ccy)
 *
 *   The ¥ stylistic mirror is the same number for every country (driven by
 *   settings.jpy_rate in v-price.liquid). It is NOT a real JPY price - it's
 *   brand-stylistic only. The exception is JP, where ¥ IS native, so we
 *   promote it to MAIN and put USD in COMPARE so the line doesn't look
 *   redundant.
 *
 *   Native rounding rule: ceil to next multiple of 5 in the native currency.
 *   Same rule for every currency including JPY/KRW. Integer output, no decimals.
 *     $30.12 → $35,  £24.10 → £25,  ¥9,247 → ¥9,250,  ₩39,852 → ₩39,855.
 *
 *   Lazy flag loading: country picker rows ship with `data-flag-src`
 *   placeholders. On first picker open, JS upgrades them to real <img>s
 *   from flagcdn.com. Zero flag traffic for visitors who never open the
 *   picker (LCP win).
 */
(function () {
  // ─── country → currency map (matches Shopify International zone, 26 ctry) ─
  var COUNTRY_TO_CCY = {
    IN: 'INR',
    US: 'USD', GB: 'GBP', AE: 'AED', CA: 'CAD', AU: 'AUD', SG: 'SGD',
    JP: 'JPY', KR: 'KRW', HK: 'HKD', MY: 'MYR', NZ: 'NZD',
    DE: 'EUR', FR: 'EUR', NL: 'EUR', IT: 'EUR', ES: 'EUR', BE: 'EUR', IE: 'EUR',
    SE: 'SEK', NO: 'NOK', CH: 'CHF',
    SA: 'SAR', QA: 'QAR', KW: 'KWD', OM: 'OMR', BH: 'BHD'
  };

  // CCY_DISPLAY = the literal text put into the .v-price__cur slot.
  // Symbols, not ISO codes (this supersedes the 2026-05-29 "ISO everywhere"
  // rule, which is why that note is gone rather than sitting here
  // contradicting the table below). A price reads as a price with its own sign in
  // front of it. Where a currency has no distinct Latin sign - the Gulf
  // currencies, and the dollars that would all collide on a bare "$" - the
  // code is kept or qualified, because an ambiguous symbol is worse than a
  // clear abbreviation.
  var CCY_DISPLAY = {
    INR: '₹',   USD: '$',    GBP: '£',   EUR: '€',   JPY: '¥',   KRW: '₩',
    AED: 'AED', CAD: 'CA$',  AUD: 'A$',  SGD: 'S$',  HKD: 'HK$',
    MYR: 'RM',  NZD: 'NZ$',  SEK: 'kr',  NOK: 'kr',  CHF: 'CHF',
    SAR: 'SAR', QAR: 'QAR',  KWD: 'KWD', OMR: 'OMR', BHD: 'BHD'
  };

  var DEFAULT_COUNTRY = 'IN';
  var RATE_TTL_MS = 24 * 60 * 60 * 1000;
  var CACHE_KEY = 'tenzen_fx_inr_v2';
  // Fallback rates (INR per 1 unit of foreign ccy). Used only if API down.
  // Approximate mid-2026 - refreshed by the live API on first success.
  var FALLBACK_INR_PER_UNIT = {
    USD: 83,    GBP: 105,   EUR: 90,    JPY: 0.55,   KRW: 0.062,
    AED: 22.6,  CAD: 60,    AUD: 54,    SGD: 62,     HKD: 10.6,
    MYR: 19,    NZD: 50,    SEK: 7.9,   NOK: 7.6,    CHF: 92,
    SAR: 22.1,  QAR: 22.8,  KWD: 270,   OMR: 215,    BHD: 220
  };

  // ─── country resolution: 4-layer fallback ───────────────────────────────
  // Priority (highest → lowest):
  //   1. `?c=XX` query param  - shareable URL carries the country. Anyone
  //      opening a friend's `?c=CA` link sees CAD prices regardless of
  //      where they're physically located. This is the answer to "what
  //      currency does a shared Canada link show on Indian browser?"
  //   2. localStorage `tenzen_country` - user has explicitly picked.
  //   3. Shopify-detected country (Shopify.country, IP-based) - no picker,
  //      no shared link, but Shopify saw their IP and figured them out.
  //   4. `DEFAULT_COUNTRY` (IN) - last resort.
  //
  // Layer 1 also writes through to localStorage so subsequent navigation
  // (clicking around the site without the param) persists the choice.
  function readCountryParam() {
    try {
      var p = new URLSearchParams(window.location.search).get('c');
      if (p) {
        p = p.toUpperCase();
        if (/^[A-Z]{2}$/.test(p) && COUNTRY_TO_CCY[p]) return p;
      }
    } catch (_) {}
    return null;
  }
  function readShopifyDetected() {
    try {
      if (window.Shopify && Shopify.country) {
        var c = String(Shopify.country).toUpperCase();
        if (/^[A-Z]{2}$/.test(c) && COUNTRY_TO_CCY[c]) return c;
      }
    } catch (_) {}
    return null;
  }
  function getCountry() {
    // ─── Domain-level hard override (highest priority) ────────────────────
    // The tenzen.in hostname is the India-only market. Any stale
    // localStorage value (e.g. user picked JP on shop.tenzen.in, then
    // navigated back to tenzen.in) MUST NOT survive here, otherwise the
    // India domain renders foreign-currency prices. Previously the host
    // check sat at the very bottom as a last-resort default and lost to
    // localStorage - that caused the "switched to JP then came back to
    // India, prices still in ¥" bug. Domain identity wins everything.
    try {
      if (window.location.hostname === 'tenzen.in') {
        // Heal stale storage so the picker UI, currency rewriter, and
        // any other reader of tenzen_country all agree.
        try { localStorage.setItem('tenzen_country', 'IN'); } catch (_) {}
        return 'IN';
      }
    } catch (_) {}

    var fromParam = readCountryParam();
    if (fromParam) {
      // Persist the param's intent so subsequent same-session navigation
      // (without the ?c=) stays consistent.
      try { localStorage.setItem('tenzen_country', fromParam); } catch (_) {}
      return fromParam;
    }
    try {
      var c = localStorage.getItem('tenzen_country');
      if (c && /^[A-Z]{2}$/.test(c) && COUNTRY_TO_CCY[c]) return c;
    } catch (_) {}
    var detected = readShopifyDetected();
    if (detected) return detected;
    // Last-resort default on shop.tenzen.in: US so a default foreign
    // visitor doesn't see jarring INR.
    try {
      if (window.location.hostname === 'shop.tenzen.in') return 'US';
    } catch (_) {}
    return DEFAULT_COUNTRY;
  }
  function setCountry(code) {
    try { localStorage.setItem('tenzen_country', code); } catch (_) {}
  }

  // ─── FX rate cache + fetch ───────────────────────────────────────────────
  // open.er-api.com returns ALL rates in one call. We cache the full table.
  function getCachedRates() {
    try {
      var raw = localStorage.getItem(CACHE_KEY);
      if (!raw) return null;
      var data = JSON.parse(raw);
      if (!data || !data.ts || !data.rates) return null;
      if (Date.now() - data.ts > RATE_TTL_MS) return null;
      return data.rates;
    } catch (_) { return null; }
  }
  function setCachedRates(rates) {
    try { localStorage.setItem(CACHE_KEY, JSON.stringify({ ts: Date.now(), rates: rates })); } catch (_) {}
  }
  function fallbackRates() {
    // Convert FALLBACK_INR_PER_UNIT (INR per 1 ccy) to API shape (ccy per 1 INR).
    var out = {};
    Object.keys(FALLBACK_INR_PER_UNIT).forEach(function (ccy) {
      out[ccy] = 1 / FALLBACK_INR_PER_UNIT[ccy];
    });
    return out;
  }
  function fetchRates() {
    var cached = getCachedRates();
    if (cached) return Promise.resolve(cached);
    return fetch('https://open.er-api.com/v6/latest/INR')
      .then(function (r) { return r.json(); })
      .then(function (data) {
        if (data && data.result === 'success' && data.rates) {
          setCachedRates(data.rates);
          return data.rates;
        }
        return fallbackRates();
      })
      .catch(function () { return fallbackRates(); });
  }

  // ─── price math ──────────────────────────────────────────────────────────
  // Returns integer in the native currency, rounded UP to nearest 5.
  function nativeFromCents(cents, ratesInrTo, targetCcy) {
    var inr = cents / 100;
    var ratePerInr = ratesInrTo[targetCcy];
    if (!ratePerInr) return null;
    var raw = inr * ratePerInr;
    return Math.ceil(raw / 5) * 5;
  }

  // ─── DOM rewriter ────────────────────────────────────────────────────────
  // Walks every .v-price[data-price-cents] and rewrites the LEFT half to
  // native currency. For JP visitors, additionally swaps MAIN/COMPARE
  // so ¥ becomes the dominant slot.
  function applyAll(rates, country) {
    var ccy = COUNTRY_TO_CCY[country];
    if (!ccy || ccy === 'INR') return; // India = server default, no-op

    var displaySym = CCY_DISPLAY[ccy] || ccy;
    var isJp = country === 'JP';

    // The bag is INR-only, always. Everywhere else on the site a native
    // figure helps a visitor size up a price; inside the bag it would be a
    // number they are about to be charged in a currency we cannot take, so
    // the drawer is excluded from the rewrite and keeps the server's INR.
    // `snippets/currency-notice.liquid` explains that switch on open.
    // Filtered in JS rather than with `:not([data-cart-drawer] ...)` so it
    // behaves the same on every browser this store sees.
    var nodes = Array.prototype.filter.call(
      document.querySelectorAll('.v-price[data-price-cents]'),
      function (el) { return !el.closest('[data-cart-drawer]'); }
    );
    nodes.forEach(function (priceEl) {
      var cents = Number(priceEl.getAttribute('data-price-cents'));
      if (!cents || isNaN(cents)) return;

      var curEl = priceEl.querySelector('.v-price__current');
      var jpEl  = priceEl.querySelector('.v-price__jp');
      if (!curEl) return;

      var curCur = curEl.querySelector('.v-price__cur');
      var curNum = curEl.querySelector('.v-price__num');
      if (!curCur || !curNum) return;

      if (isJp) {
        // JP special: MAIN = JPY-labeled yen (server-rendered ¥ value from
        // the right slot, promoted to MAIN), COMPARE = $ USD. Per locked
        // spec 2026-05-29: MAIN slot uses the 3-letter code 'JPY', not the
        // ¥ glyph - symbols are reserved for the right-mirror only.
        // Read the server's yen once and keep it on the element. This runs
        // again on every DOM change (the observer below), and by then the
        // right-hand slot already reads "/ $ 40" - re-reading it turned
        // JPY 12,946 into JPY 40.
        var serverYen = priceEl.getAttribute('data-yen');
        if (serverYen == null) {
          serverYen = jpEl ? (jpEl.textContent.replace(/[^\d]/g, '') || '') : '';
          priceEl.setAttribute('data-yen', serverYen);
        }
        if (serverYen) {
          curCur.textContent = 'JPY';
          curNum.textContent = Number(serverYen).toLocaleString('en-IN');
        }
        if (jpEl) {
          var usdForJp = nativeFromCents(cents, rates, 'USD');
          if (usdForJp != null) {
            jpEl.textContent = '/ $ ' + usdForJp.toLocaleString('en-IN');
          }
        }
      } else {
        // Standard non-IN/non-JP visitor: rewrite LEFT half to native ccy.
        var native = nativeFromCents(cents, rates, ccy);
        if (native == null) return;
        curCur.textContent = displaySym;
        curNum.textContent = native.toLocaleString('en-IN');
      }
      curCur.setAttribute('data-no-translate', '');
      curNum.setAttribute('data-no-translate', '');

      // Compare-at strike-through, if present, follows the same conversion
      // as MAIN (no JP swap needed - it's just the sale price).
      var compareEl = priceEl.querySelector('.v-price__compare');
      if (compareEl) {
        var compareCents = Number(priceEl.getAttribute('data-compare-cents'));
        if (compareCents && !isNaN(compareCents)) {
          var compareCur = compareEl.querySelector('.v-price__cur');
          var compareNum = compareEl.querySelector('.v-price__num');
          if (compareCur && compareNum) {
            var cmpTargetCcy = isJp ? 'JPY' : ccy;
            var cmp = nativeFromCents(compareCents, rates, cmpTargetCcy);
            if (cmp != null) {
              compareCur.textContent = isJp ? 'JPY' : displaySym;
              compareNum.textContent = cmp.toLocaleString('en-IN');
              compareCur.setAttribute('data-no-translate', '');
              compareNum.setAttribute('data-no-translate', '');
            }
          }
        }
      }

      if (jpEl) jpEl.setAttribute('data-no-translate', '');
    });
  }

  // ─── lazy flag loader for the picker ─────────────────────────────────────
  // Picker rows ship with `data-flag-src` placeholder spans. On first time
  // the country panel opens, upgrade them to real <img>s from flagcdn.com.
  function upgradeFlags() {
    var slots = document.querySelectorAll('.v-country__flag[data-flag-src]');
    slots.forEach(function (slot) {
      var src = slot.getAttribute('data-flag-src');
      if (!src) return;
      var img = document.createElement('img');
      img.className = 'v-flag';
      img.src = src;
      img.alt = '';
      img.width = 22;
      img.height = 22;
      img.loading = 'lazy';
      img.decoding = 'async';
      slot.innerHTML = '';
      slot.appendChild(img);
      slot.removeAttribute('data-flag-src');
    });
  }

  // Mark the selected row + update the header trigger flag.
  function markSelected(country) {
    var rows = document.querySelectorAll('[data-country-select]');
    rows.forEach(function (r) {
      r.classList.toggle('is-selected', r.getAttribute('data-country-select') === country);
    });
  }

  // ─── country → URL routing ──────────────────────────────────────────────
  // Two hostnames in play:
  //   tenzen.in       → India market only (no locale prefix possible).
  //   shop.tenzen.in  → International market, locale-prefixed for non-EN.
  //
  // COUNTRY_TO_LOCALE maps each picker country to the Shopify locale that
  // should serve its experience on shop.tenzen.in. English-speaking
  // countries map to '' (default English on shop.tenzen.in/ with no path
  // prefix); their currency is still set per-country via ?c= + currency.js.
  var COUNTRY_TO_LOCALE = {
    IN: 'in',         // sentinel - handled specially below (route to tenzen.in/)
    US: '', GB: '', AE: '', CA: '', AU: '', SG: '', HK: '', MY: '',
    NZ: '', IE: '', SA: '', QA: '', KW: '', OM: '', BH: '',
    JP: 'ja', KR: 'ko',
    DE: 'de', CH: 'de',
    FR: 'fr', BE: 'fr',
    ES: 'es',
    IT: 'it',
    NL: 'nl',
    SE: 'no',  // closest available locale (Norwegian and Swedish are mutually intelligible)
    NO: 'no'
  };

  // Build the target URL for a picker country selection.
  // Preserves the user's current path (e.g. /products/x) on the new host.
  function buildTargetUrl(country) {
    var path = window.location.pathname;
    // Strip any existing /xx/ locale prefix (so switching between non-EN
    // locales doesn't accumulate paths like /de/ja/products/x).
    path = path.replace(/^\/(ja|ko|fr|ru|de|es|it|nl|no)(\/|$)/, '/');
    if (country === 'IN') {
      // India → bare root domain. Currency.js no-op on tenzen.in.
      return 'https://tenzen.in' + path;
    }
    var locale = COUNTRY_TO_LOCALE[country];
    if (locale == null) locale = '';   // unknown country → English fallback
    var prefix = locale ? ('/' + locale) : '';
    return 'https://shop.tenzen.in' + prefix + path + '?c=' + country;
  }

  // ─── init ────────────────────────────────────────────────────────────────
  function init() {
    var country = getCountry();
    markSelected(country);

    // Picker click → navigate to the proper hostname + locale prefix + ?c=.
    document.addEventListener('click', function (e) {
      var btn = e.target.closest('[data-country-select]');
      if (!btn) return;
      var next = btn.getAttribute('data-country-select');
      if (!next || next === getCountry()) return;
      setCountry(next);
      var target = buildTargetUrl(next);
      window.location.assign(target);
    });

    // Lazy-load picker flags when the country panel opens. The header script
    // toggles class `is-country-open` on the .v-header element when the
    // picker icon is tapped. If that hook doesn't exist, fall back to
    // hover/click on the panel itself.
    var header = document.querySelector('.v-header');
    var panel = document.getElementById('v-header-country');
    var flagsUpgraded = false;
    function maybeUpgrade() {
      if (flagsUpgraded) return;
      flagsUpgraded = true;
      upgradeFlags();
    }
    if (header) {
      var mo = new MutationObserver(function () {
        if (header.classList.contains('is-country-open') ||
            (panel && !panel.hidden && panel.offsetParent !== null)) {
          maybeUpgrade();
        }
      });
      mo.observe(header, { attributes: true, attributeFilter: ['class'] });
    }
    // Fallback: any touch/click anywhere near the country trigger upgrades.
    document.addEventListener('click', function (e) {
      if (e.target.closest('[data-toggle-country], #v-header-country')) maybeUpgrade();
    }, { passive: true });

    // India visitor → no rewrite of prices. Just mark ¥ slots no-translate.
    if (country === 'IN') {
      document.querySelectorAll('.v-price .v-price__jp').forEach(function (el) {
        el.setAttribute('data-no-translate', '');
      });
      window.TenzenCurrency = {
        country: country, currency: 'INR', refresh: function () {}
      };
      return;
    }

    // Non-India: fetch rates + apply.
    function refresh() {
      return fetchRates().then(function (rates) {
        if (!rates) return;
        applyAll(rates, country);
      });
    }
    window.TenzenCurrency = {
      country: country,
      currency: COUNTRY_TO_CCY[country],
      refresh: refresh
    };
    refresh();

    // Re-apply when cart drawer / PDP rewrites price HTML.
    if ('MutationObserver' in window) {
      var t = null;
      var observer = new MutationObserver(function () {
        if (t) return;
        t = setTimeout(function () {
          t = null;
          var cached = getCachedRates();
          if (cached) applyAll(cached, country);
          else refresh();
        }, 60);
      });
      observer.observe(document.body, { childList: true, subtree: true });
    }
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
